import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import TruncatedText from 'src/components/TruncatedText';
import LoadingRows from 'src/components/Table/LoadingRows';
import NoDataCell from 'src/components/NoDataCell';
import TableCard from 'src/components/Table/TableCard';
import { DetailCard } from 'src/components/summary/DetailCard';
import { SummaryGrid } from 'src/components/summary/SummaryGrid';
import { SummaryValue } from 'src/components/summary/SummaryValue';
import { PORTAL_ROUTE } from 'src/constants/routes';
import DeployableArtifactList from 'src/features/deployableArtifacts/DeployableArtifactList';
import { useInfinitePolicyVersionDevices, usePolicyDetails } from 'src/hooks/environmentWdacPolicy';
import { DEVICES_PREVIOUS_PATH } from 'src/pages/devices/deviceDetails/constant';
import { formatDate } from 'src/utils/formatTime';
import { getBasePolicyModeLabel } from 'src/utils/enums/basePolicyMode';
import { getWdacPolicyPurposeLabel } from 'src/utils/enums/wdacPolicyPurpose';
import { WDAC_POLICY_TYPE, getWdacPolicyTypeLabel } from 'src/utils/enums/wdacPolicyType';
import { getWdacStatusLabel } from 'src/utils/enums/wdacStatus';
import { BasePolicyLink, DetectedHash, LatestChip } from '../../PolicyVersionParts';
import PolicyVersionRules from './PolicyVersionRules';

const HEADER_SX = { fontSize: '0.7rem', fontWeight: 600, py: 0.75 };
const CELL_SX = { fontSize: '0.7rem', py: 0.75, lineHeight: 1.3 };
const ROW_SX = { cursor: 'pointer', '&:hover': { backgroundColor: '#565656' } };
const MONOSPACE_SX = { fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all' };

const COLUMNS = [
  { key: 'deviceName', label: 'Device Name', width: '46%' },
  { key: 'wdacDeviceStatus', label: 'WDAC Status', width: '22%' },
  { key: 'lastAppliedDate', label: 'Last Applied', width: '32%' },
];

const yesNo = value => (value ? 'Yes' : 'No');
const isBase = policy => policy.policyType === WDAC_POLICY_TYPE.BasePolicy;
const isSupplemental = policy => !isBase(policy);

// A base has a mode and, when the portal built it, the policy info ID it was given. A supplemental
// instead extends a base, for a purpose when the portal manages it. Whether a version is the
// portal's latest only means something on a policy the portal manages.
const FIELDS = [
  { key: 'policyName', label: 'Name', render: version => version.policyName },
  {
    key: 'policyId',
    label: 'Policy ID',
    render: (version, policy) => (
      <Box component="span" sx={MONOSPACE_SX}>
        {policy.policyId}
      </Box>
    ),
  },
  {
    key: 'policyType',
    label: 'Type',
    render: (version, policy) => getWdacPolicyTypeLabel(policy.policyType),
  },
  {
    key: 'basePolicyMode',
    label: 'Mode',
    appliesTo: isBase,
    render: version => getBasePolicyModeLabel(version.basePolicyMode),
  },
  {
    key: 'policyInfoId',
    label: 'Policy Info ID',
    appliesTo: isBase,
    render: version => version.portalOptions?.policyInfoId,
  },
  {
    key: 'basePolicy',
    label: 'Base Policy',
    appliesTo: isSupplemental,
    render: version => <BasePolicyLink version={version} />,
  },
  {
    key: 'policyPurpose',
    label: 'Purpose',
    appliesTo: isSupplemental,
    render: (version, policy) =>
      policy.policyPurpose ? getWdacPolicyPurposeLabel(policy.policyPurpose) : 'N/A',
  },
  {
    key: 'devicesCount',
    label: 'Devices',
    render: version => (version.devicesCount ?? 0).toLocaleString(),
  },
  { key: 'createdAt', label: 'Date Added', render: version => formatDate(version.createdAt) },
  {
    key: 'policyTimestamp',
    label: 'Policy Timestamp',
    render: version => (version.policyTimestamp ? formatDate(version.policyTimestamp) : null),
  },
  {
    key: 'isLatestManaged',
    label: 'Latest Managed',
    appliesTo: policy => policy.isPortalManaged,
    render: version => yesNo(version.isLatestManaged),
  },
];

const CardSection = ({ title, children }) => (
  <DetailCard compact>
    <Stack spacing={1.5}>
      <Typography variant="subtitle2">{title}</Typography>
      {children}
    </Stack>
  </DetailCard>
);

// Everything known about one version of this policy: its facts, how it was built, the artifacts
// it was published as, and the devices that have reported it.
const PolicyVersionModal = ({ open, version, onClose }) => {
  const { policyId } = useParams();
  const navigate = useNavigate();
  const { data: policy } = usePolicyDetails({ envPolicyId: policyId });

  const devicesQuery = useInfinitePolicyVersionDevices({
    envPolicyId: policyId,
    versionId: version?.id,
    enabled: open,
  });

  const devices = useMemo(
    () => devicesQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [devicesQuery.data],
  );

  const [sentryRef] = useInfiniteScroll({
    loading: devicesQuery.isFetchingNextPage,
    hasNextPage: !!devicesQuery.hasNextPage,
    onLoadMore: devicesQuery.fetchNextPage,
    rootMargin: '0px 0px 200px 0px',
  });

  const openDevice = device => {
    sessionStorage.setItem(
      DEVICES_PREVIOUS_PATH,
      `${window.location.pathname}${window.location.search}`,
    );
    navigate(`${PORTAL_ROUTE}/devices/${device.deviceId}/general`);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#3f3f3f' } }}>
      <DialogTitle sx={{ pr: 6, pb: 1 }}>
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
          <span>Version {version?.policyVersion}</span>
          {version ? <LatestChip version={version} /> : null}
        </Stack>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: '#CFCFCF' }}>
          <Iconify icon="mdi:close" width={20} height={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {version && policy ? (
          <Stack spacing={2}>
            <DetailCard compact>
              <Stack spacing={2}>
                <SummaryGrid compact>
                  {FIELDS.filter(field => !field.appliesTo || field.appliesTo(policy)).map(
                    field => (
                      <SummaryValue key={field.key} label={field.label} compact>
                        {field.render(version, policy)}
                      </SummaryValue>
                    ),
                  )}
                </SummaryGrid>
                <SummaryValue label="Detected Hash" compact>
                  <DetectedHash version={version} />
                </SummaryValue>
              </Stack>
            </DetailCard>

            <CardSection title="Rules">
              <PolicyVersionRules version={version} isSupplemental={isSupplemental(policy)} />
            </CardSection>

            <CardSection title="Artifacts">
              <DeployableArtifactList
                artifacts={version.artifacts}
                emptyText="No artifacts recorded for this version."
                downloadable
              />
            </CardSection>

            <TableCard title="Devices" maxHeight={340} edgePadding={2}>
              <Table stickyHeader size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead sx={{ backgroundColor: '#3f3f3f' }}>
                  <TableRow>
                    {COLUMNS.map(column => (
                      <TableCell key={column.key} sx={{ ...HEADER_SX, width: column.width }}>
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody sx={{ backgroundColor: '#4B4B4B' }}>
                  {devicesQuery.isLoading ? (
                    <LoadingRows columnCount={COLUMNS.length} rowCount={5} />
                  ) : (
                    <>
                      {devices.map((device, index) => (
                        <TableRow
                          key={device.deviceId}
                          ref={index === devices.length - 1 ? sentryRef : null}
                          onClick={() => openDevice(device)}
                          sx={ROW_SX}>
                          <TableCell sx={CELL_SX}>
                            <TruncatedText value={device.deviceName || '—'} bold />
                          </TableCell>
                          <TableCell sx={CELL_SX}>
                            <TruncatedText
                              value={getWdacStatusLabel(device.wdacDeviceStatus)}
                              muted
                            />
                          </TableCell>
                          <TableCell sx={CELL_SX}>
                            <TruncatedText value={formatDate(device.lastAppliedDate)} muted />
                          </TableCell>
                        </TableRow>
                      ))}
                      {devicesQuery.isFetchingNextPage && (
                        <LoadingRows columnCount={COLUMNS.length} rowCount={2} />
                      )}
                      {!devicesQuery.isFetching && !devices.length && (
                        <NoDataCell colSpan={COLUMNS.length}>
                          No devices have reported this version.
                        </NoDataCell>
                      )}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableCard>
            <Typography variant="caption" sx={{ color: '#9CA3AF' }}>
              Devices that have reported this version at any point, most recent first, whether or
              not it is still active on them. Archived and revoked devices are excluded.
            </Typography>
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default PolicyVersionModal;
